import React, { useState, useEffect, useRef } from 'react';
import { array, bool, func, object, shape, string } from 'prop-types';
import { SizeChartLink, ContactUsCTA, PromoPrice } from '../../../index';
import { SizeSelect, ColoursWithLink, ProductInformation, PartNumber, CTAButtons } from '@ynap/product-details-page';
import { trackAccordion, trackSizeSelect, upsellNavigation, trackAccordionLinkClick, trackEmailUsCTA, trackCallUsCTA } from '../../../analytics';
import EditorialAccordion from '../../../EditorialAccordion/EditorialAccordion';
import { DeliveryAndReturnsSummary } from '../../../DeliveryAndReturns';
import DeliveryAndReturnsLegal from '../../../DeliveryAndReturns/DeliveryAndReturnsLegal';
import { SingleBadge, TransitionSingleBadge } from '@ynap/badge';
import { PriceWithSchema, promotionType } from '@ynap/price';
import { productSelectors } from '@ynap/product-state';
import BEMHelper from '@ynap/bem-helper';
import { editorialIds } from '../../../EditorialAccordion/getEditorials';
import Notification from '@ynap/notification';
import { useConfig } from '@ynap/immutables-provider';
import ReactMarkdown from 'markdown-to-jsx';
import { OptimizelyFeature } from '@ynap/optimizely-utils';
import { KlarnaMessage } from './KlarnaMessage';
import ProductDetailsFullBleedWrapper from './ProductDetailsWrapper';
import LoyaltyBadgeNotification from '@ynap/loyalty-badge-notification';
import CompanyInformation from '../CompanyInformation';
import Skeleton from '@ynap/skeleton';

const { getProductLinkByPartNumber } = productSelectors;
const bem = new BEMHelper('ProductDetails');

const WrapperComp = ({ children, showBleedLayout }) => {
    const [isMobileOrTablet, setIsMobileOrTablet] = useState(false);

    useEffect(() => {
        const media = window.matchMedia('(min-width: 0px) and (max-width: 1023px)');
        setIsMobileOrTablet(media.matches);

        const resizeHandler = () => {
            setIsMobileOrTablet(media.matches);
        };

        media.addEventListener('change', resizeHandler);

        return () => {
            media.removeEventListener('change', resizeHandler);
        };
    }, []);

    return showBleedLayout && !isMobileOrTablet ? <ProductDetailsFullBleedWrapper>{children}</ProductDetailsFullBleedWrapper> : <>{children}</>;
};

export default function ProductDetails(props) {
    const [activeEditorialSection, setActiveEditorialSection] = useState();
    const [applyNotificationModifier, setApplyNotificationModifier] = useState('');
    const [toggle, setToggle] = useState(false);
    const [noSizeNotification, setNoSizeNotification] = useState(null);
    const [productLink, setProductLink] = useState(null);
    const [isSizeFitScroll, setIsSizeFitScroll] = useState(false);
    const [disableOneSizeLeft, setDisableOneSizeLeft] = useState(true);
    const [applyModelMarginStyle, setApplyModelMarginStyle] = useState(false);
    const [userSummary, setUserSummary] = useState(undefined);
    const [isSizeChartAndMessageLoading, setIsSizeChartAndMessageLoading] = useState(true);
    const [shouldDisableSizeChartAndMessage, setShouldDisableSizeChartAndMessage] = useState(false);

    const config = useConfig();
    const [width, setWidth] = useState(0);
    const sizeSelectRef = useRef(null);
    const accordionRef = useRef(null);

    const {
        addToCart,
        addToWishlist,
        fetchWishlists,
        wishlists,
        product,
        messages,
        changeSelectedSku,
        locale,
        country,
        generateListingUrl,
        actionsLoading: { addToCartLoading, addToWishlistLoading, fetchWishlistsLoading },
        actionsErrors: errors,
        plpUrlTemplate,
        baseUrl,
        urlConfig,
        internal_urls,
        customerCareUrl,
        sizeLabelToggle,
        config: {
            head: {
                pdp: {
                    enableMultipleWishlists,
                    includeCartWithAddToBag,
                    preventSkuNotification,
                    scrollToSizeSelectMultipleSizes = false,
                    hideSizeConversionMessageByGender,
                    enableDeliveryAndReturnCustomMessage,
                    disableSizeChartForDesignerIdsAndCategories,
                    companyInformationCountryCode,
                    deliveryAndReturnLegalMessageEnabledCountries,
                } = {},
                minimumPriceCountries = [],
            } = {},
        } = {},
        features = {},
        sizeAndFitInformation,
        measurements,
        setSizeChartModal,
        tracking: { trackedAddToBag, trackedAddToBagError, trackedGoToBag, trackedAddToWishlist, trackedAddToWishlistError, safeTrackEvent, trackEvent },
        setModal,
        ogMetaData,
        sizeGridFeatureToggle,
        designerLinkOnClick,
        language,
        redirectToWishlist,
        pdpUrlTemplate,
        setUser,
        optimizely,
        brandId,
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onModelSizeClick = () => {},
        analytics,
        isEip,
        stickyCta = false,
        promoPrice = {},
        deliveryReturns = {},
    } = props;
    const {
        name,
        designerName,
        designerIdentifier,
        designerSeo,
        colourSwatches,
        skus,
        sizeLabel,
        productNotes,
        madeIn,
        label: colourLabel,
        selectedSku,
        buyable,
        sizeConversion,
        sizeMeasurement,
        measurementType,
        price,
        badges: { selectedProductColorFirstBadge, selectedProductSizeBadge } = {},
        selectedSize,
        isOneSize,
        fullfilledBy,
        hasMeaningfulSize,
        consideredBadge,
        consideredLabels,
        sustainabilityDisclaimers,
        showKlarnaMessage,
        masterCategory,
        finalSale,
        fjwThresholdHandler,
        tracking,
        saleExclusive,
        sizeScheme,
        imageViews,
        imageTemplate,
    } = product;

    const { marketingProperties = {}, promotionApplied = 'none' } = promoPrice;
    const hasPromoBadge = promotionApplied === promotionType.MANUAL; // TODO this can be simply passed to components as the promotion type
    const hasAutoApplyPromo = promotionApplied === promotionType.AUTO;
    const { loyaltySegment, loyaltyConsent, personalShopperDetails: { imageURL } = {} } = userSummary ?? {};

    const cartButton = addToCart && { addToCart: trackedAddToBag, failedAddToBag: trackedAddToBagError, goToCart: trackedGoToBag, addToCartLoading };
    const wishlistButton = addToWishlist && {
        fetchWishlists,
        fetchWishlistsLoading,
        addToWishlist: trackedAddToWishlist,
        failedAddToWishlist: trackedAddToWishlistError,
        addToWishlistLoading,
        wishlists,
        goToWishlist: redirectToWishlist,
    };

    const { isWatchExperts, isMRPJewelry, emailUsCTA = null, callUsCTA = null } = features;

    const selectedProduct = {
        personalShopperNotification: buyable?.notification?.contactUsCTA,
        eipPersonalShopperNotification: buyable?.notification?.eip?.contactUsCTA,
        name: product.name,
        type: buyable?.notification?.type,
        canAddToBag: buyable?.canAddToBag,
        oneSize: skus && skus.length === 1,
        isWatchExperts: isWatchExperts,
        isMRPJewelry: isMRPJewelry,
    };

    const mrpHandler = brandId === 'mrp';
    const napHandler = brandId === 'nap';
    const tonHandler = brandId === 'theoutnet';

    const isProductSizeDisplayable = !!skus?.length;
    const hideSizeSelect = (features?.hideSizeSelect && selectedProduct.oneSize) || (!buyable?.canAddToBag && !buyable?.canAddToWishlist);
    const showColour = !features?.hideColour;
    const isExclusiveBadge = selectedProductColorFirstBadge?.key === 'BADGE_EXCLUSIVE';

    let showContactUsCTABottom = !emailUsCTA?.Component && buyable?.notification?.type === 'showContactUsCTABottom';
    if (napHandler || mrpHandler) {
        showContactUsCTABottom =
            emailUsCTA?.Component && buyable?.notification?.type === 'showContactUsCTABottom' && selectedProduct.eipPersonalShopperNotification;
    }
    const showContactUsCTATop =
        !emailUsCTA?.Component && (buyable?.notification?.contactUsCTA || buyable?.notification?.eip?.contactUsCTA) && !showContactUsCTABottom;

    const showCallUsCTA = callUsCTA?.Component && !emailUsCTA?.Component && !showContactUsCTATop;

    const modifier = buyable?.canAddToBag || buyable?.canAddToWishlist ? '' : 'hideMargin';
    const showBleedLayout = config.head.pdp?.fullBleedLayout?.enabled;

    const scrollToSizeSelect = () => {
        if (sizeSelectRef.current) {
            const { offsetTop, clientHeight } = sizeSelectRef.current;
            const isMobileOrTablet = window.matchMedia('(min-width: 0px) and (max-width: 1023px)').matches;
            // The container for this element has a position sticky only on Desktop on FB mode
            if (showBleedLayout && !isMobileOrTablet) {
                const sizeSelectPosition = sizeSelectRef.current.getBoundingClientRect();
                // If the element is out of the viewport
                if (sizeSelectPosition.top <= 0) {
                    // Scrolls the element offset and adds 60px to center the sizeSelect on the screen
                    const scrollToTop = document.documentElement.scrollTop - Math.abs(sizeSelectPosition.top) - 60;
                    window.scrollTo({ top: scrollToTop, left: 0, behavior: 'smooth' });
                }
            } else {
                window.scrollTo({ top: offsetTop - clientHeight - 60, left: 0, behavior: 'smooth' });
            }
        }
    };

    const scrollToSizeFitAccordion = () => {
        setActiveEditorialSection(editorialIds.SIZE_AND_FIT);
        const { offsetTop, clientHeight } = accordionRef.current;
        window.scrollTo({ top: offsetTop - clientHeight - 46, left: 0, behavior: 'smooth' });
    };

    // eslint-disable-next-line react/prop-types
    const RenderSizeChartLink = ({ sizeConversion, placeModifier }) => (
        <SizeChartLink
            setSizeChartModal={setSizeChartModal}
            isOneSize={isOneSize}
            sizeConversion={sizeConversion}
            sizeMeasurement={sizeMeasurement}
            hasMeaningfulSize={hasMeaningfulSize}
            optimizely={optimizely}
            placeModifier={placeModifier}
        >
            {measurementType === 'iiMeasurements' ? messages.sizes.sizeGuide.measuringGuide() : messages.sizes.sizeGuide.viewSizeGuide()}
        </SizeChartLink>
    );

    const sizeConversionBadge =
        sizeLabel &&
        selectedSize &&
        messages.sizes.sizeConversion({
            sizeLabel: selectedSku?.size?.centralSizeLabel ? selectedSku.size.centralSizeLabel : sizeLabel,
            localLanguageType: selectedSize.name,
            localSizeValue:
                selectedSize.labels.length > 1 ? `${selectedSize.labels[0]}-${selectedSize.labels[selectedSize.labels.length - 1]}` : selectedSize.labels[0],
        });
    const isStockBadgesVisible = !!(sizeGridFeatureToggle && selectedProductSizeBadge?.label);
    const genderType = analytics?.page?.pageInfo?.gender;
    const sizeConversionBadgeDesignersIncluded = hideSizeConversionMessageByGender?.[genderType]?.includes(designerName);
    const isSizeConversionBadgeVisible = !!(sizeConversionBadge && selectedSize && !sizeConversionBadgeDesignersIncluded);
    const notificationConfig = config?.head?.pdp?.notification;
    //helm config converts strings with commas into arrays. This functions turns it back to a string
    const formatNotificationMessage = (message) => {
        if (Array.isArray(message)) {
            return message.join();
        } else {
            return message;
        }
    };
    const errorNotificationTimer = config?.head?.errorNotificationTimer;

    useEffect(() => {
        if (genderType && sizeScheme && designerIdentifier && isSizeChartAndMessageLoading) {
            setShouldDisableSizeChartAndMessage(
                disableSizeChartForDesignerIdsAndCategories?.[genderType?.toLowerCase()]?.[designerIdentifier]?.includes(sizeScheme) || false,
            );
            setIsSizeChartAndMessageLoading(false);
        } else if (!disableSizeChartForDesignerIdsAndCategories) {
            setShouldDisableSizeChartAndMessage(false);
            setIsSizeChartAndMessageLoading(false);
        }
    }, [genderType, sizeScheme, designerIdentifier]);

    useEffect(() => {
        setProductLink(getProductLinkByPartNumber(pdpUrlTemplate, locale, undefined, product.colourPartNumber));
    }, [product.colourPartNumber]);

    const hasPromoPrice = hasPromoBadge && 'promoPriceApplied';

    const stickyModifier = stickyCta ? 'stickyCta' : null;

    const selectedProductBadgeModifier = selectedProductColorFirstBadge.key ? 'selectedProductBadgeModifier' : null;

    useEffect(() => {
        const isOneSizeLeft =
            skus.map((sku) => {
                if (sku.buyable) {
                    return sku.buyable;
                }
            }).length === 1;
        isOneSizeLeft && isSizeFitScroll && activeEditorialSection !== editorialIds.SIZE_AND_FIT && scrollToSizeFitAccordion();
    }, [isSizeFitScroll]);

    useEffect(() => {
        window?.SF?.getUserSummary?.().then((userSummary) => {
            setUserSummary(userSummary);
        });
    }, []);

    const showMinimumPriceLabel = minimumPriceCountries.includes(country?.toUpperCase?.()) && typeof price?.minimumPrice === 'number';
    const isEUcountry = minimumPriceCountries.includes(country?.toUpperCase?.());

    const renderModelSize = () => {
        const findModelSize = productNotes?.sizeAndFit?.split?.(' - ').filter?.((phrase) => phrase.startsWith('Model'));
        if (!findModelSize || findModelSize.length === 0) {
            return null;
        }
        const modelMeasurements = findModelSize?.flatMap((item) => item.split(/\s*<br><br>\s*/));
        if (!modelMeasurements.length) {
            return null;
        }
        return (
            <OptimizelyFeature feature={['WEB00121_Model_size_visibility_PDP_responsive', 'WEB00079_Model_size_visibility_PDP_responsive']}>
                {(props) => {
                    const applyModelSizeInformation = ['v1_web00121', 'v1_web00079'].includes(props?.variationKey);
                    applyModelSizeInformation && setApplyModelMarginStyle(true);
                    return applyModelSizeInformation && applyModelMarginStyle ? (
                        <div className={bem('modelSizeWrapper')}>
                            <button className={bem('modelSizeButton')} onClick={onModelSizeClick} />
                            <div className={bem('modelSizeContainer')}>
                                {modelMeasurements.map((modelInfo, index) => (
                                    <div key={index} className={bem('modelSize')}>
                                        {modelInfo}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : null;
                }}
            </OptimizelyFeature>
        );
    };
    const showFreeShippingThreshold = () => {
        const freeShippingThresholds = config?.head?.pdp?.freeShipping;
        const freeShippingValue = freeShippingThresholds?.[country];
        const freeShippingMessage = messages?.product?.freeShippingThreshold?.({ value: `${price?.currency?.symbol}${freeShippingValue}` });
        const freeShippingEligible = buyable.canAddToBag && !finalSale && freeShippingMessage && freeShippingValue;
        if (!freeShippingEligible) {
            return null;
        }

        return (
            <OptimizelyFeature feature={['WEB00609_freeshipping_PDP', 'WEB00572_freeshipping_PDP', 'WEB00570_freeshipping_PDP', 'WEB00571_freeshipping_PDP']}>
                {(props) => {
                    const { variationKey } = props;
                    const showFreeShipping = ['v1_web00609', 'v1_web00572', 'v1_web00571', 'v1_web00570'].includes(variationKey);
                    if (showFreeShipping) setApplyNotificationModifier('slimMargin');
                    return showFreeShipping && <Notification type="info-no-icon" className={bem('freeShipping')} message={freeShippingMessage} />;
                }}
            </OptimizelyFeature>
        );
    };

    const { tierExpansionEnabled } = config.head;

    const shouldShowLoyaltyLink = !!(userSummary !== undefined && tierExpansionEnabled);
    const tier = loyaltyConsent === 'true' && loyaltySegment ? loyaltySegment : 'guest';
    const loyaltyNotification = messages?.loyalty?.notification({ tier, locale: typeof locale === 'string' ? locale : locale.id, isExclusiveBadge }) || '';
    const isCompanyInformationLinkEnabled = country?.includes(companyInformationCountryCode);
    const showDeliveryAndReturnsLegal = deliveryAndReturnLegalMessageEnabledCountries?.includes(country?.toUpperCase?.());

    return (
        <WrapperComp showBleedLayout={showBleedLayout}>
            <div
                className={bem('basicInfo', [
                    stickyModifier,
                    consideredBadge ? 'sustainabilityModifier' : 'productColourBadgeModifier',
                    !consideredBadge && hasPromoPrice,
                    hasAutoApplyPromo && 'autoApplyPromo',
                ])}
            >
                <ProductInformation
                    className={{
                        container: bem('productInformation'),
                        designerModifier: stickyModifier,
                        nameModifier: stickyModifier,
                        selectedProductBadgeModifier: selectedProductBadgeModifier,
                    }}
                    productId={product.colourPartNumber}
                    skuId={selectedSku && selectedSku.partNumber}
                    name={name}
                    designerName={designerName}
                    designerSeo={designerSeo}
                    locale={locale}
                    plpUrlTemplate={plpUrlTemplate}
                    messages={messages}
                    generateListingUrl={generateListingUrl}
                    baseUrl={baseUrl}
                    designerLinkOnClick={designerLinkOnClick}
                    promoPriceApplied={hasPromoPrice}
                    setWidth={(value) => setWidth(value)}
                />
                <PriceWithSchema
                    offerUrl={ogMetaData?.url}
                    details
                    {...price}
                    className={bem('price', [stickyModifier, hasPromoPrice])}
                    messages={messages}
                    locale={locale}
                    priceSuffix={messages.price?.taxIncluded && messages.price.taxIncluded()}
                    setModal={setModal}
                    showMinimumPriceLabel={showMinimumPriceLabel}
                    isEUcountry={isEUcountry}
                    designerName={designerName}
                    badgeKey={selectedProductColorFirstBadge.key}
                    country={country}
                    addMicrodata={true}
                    saleExclusive={saleExclusive}
                    promoPriceApplied={hasPromoPrice} // TODO this state can be derived from promotionApplied
                    promotionApplied={promotionApplied}
                    marketingProperties={marketingProperties}
                    hasAutoApplyPromo={hasAutoApplyPromo}
                />
                <div className={bem('promoInformation', { hasAutoApplyPromo })}>
                    <PromoPrice messages={messages} price={price} promotionApplied={promotionApplied} marketingProperties={marketingProperties} />
                </div>

                {selectedProductColorFirstBadge && !hasPromoPrice && (
                    <TransitionSingleBadge badge={selectedProductColorFirstBadge} stickyModifier={stickyModifier} promoPriceApplied={hasPromoPrice} />
                )}

                {hasPromoPrice && selectedProductSizeBadge && <TransitionSingleBadge badge={selectedProductSizeBadge} stickyModifier={stickyModifier} />}

                {consideredBadge && (
                    <SingleBadge
                        className={bem('consideredBadge')}
                        considered={true}
                        badge={{ label: consideredBadge, key: 'CONSIDERED_BADGE' }}
                        translation={messages.considered.badge()}
                        onBadgeClick={
                            tonHandler
                                ? () => {
                                      setActiveEditorialSection('CONSIDERED_INFO');
                                  }
                                : undefined
                        }
                    />
                )}
                <div className={bem('klarnaMessageVariant2', { hasPromoBadge }, 'klarnaMessage__container')}>
                    <KlarnaMessage
                        messages={messages}
                        country={country}
                        config={config}
                        showKlarnaMessage={showKlarnaMessage}
                        buyable={buyable}
                        price={price}
                        locale={locale}
                    />
                </div>
            </div>

            <div className={bem()}>
                {(isStockBadgesVisible || isSizeConversionBadgeVisible) && (
                    <div className={bem('stockSizeConversionBadgesWrapper')}>
                        {isStockBadgesVisible && <SingleBadge className={bem('stockBadges')} badge={selectedProductSizeBadge} hasBackGround={false} />}

                        {isSizeConversionBadgeVisible && !shouldDisableSizeChartAndMessage && (
                            <SingleBadge className={bem('sizeConversion')} badge={{ label: sizeConversionBadge }} hasBackGround={false} />
                        )}
                    </div>
                )}
                {showColour && (
                    <ColoursWithLink
                        className={bem('colours')}
                        messages={messages}
                        colours={colourSwatches}
                        colourLabel={colourLabel}
                        onClick={(partNumber, position) =>
                            safeTrackEvent(upsellNavigation, partNumber, position, 'view more colours in this product', 'alt colours')
                        }
                    />
                )}

                {shouldDisableSizeChartAndMessage ? null : (
                    <OptimizelyFeature feature={['WEB00552_SizeTiles_PDP']}>
                        {(props) => {
                            const variation = props?.variationKey;

                            return isSizeChartAndMessageLoading ? (
                                <Skeleton count={1} containerClassName={bem('sizeChartLinkSkeletonContainer')} className={bem('sizeChartLinkSkeleton')} />
                            ) : (
                                <RenderSizeChartLink sizeConversion={sizeConversion} placeModifier={variation === 'v1_web00552_exposed' && 'placeModifier'} />
                            );
                        }}
                    </OptimizelyFeature>
                )}

                {isProductSizeDisplayable && !hideSizeSelect && (
                    <OptimizelyFeature
                        feature={[
                            'web00025_sizeconversionmessaging_plp_nap',
                            'web00082_sizeconversionmessaging_plp_nap_mobile',
                            'web00083___size_conversion_messaging___mrp___desktop',
                            'web00085_sizeconversionmessaging_pdp_mrp_mobile',
                            'WEB00552_SizeTiles_PDP',
                        ]}
                    >
                        {(props) => {
                            const experiment = props?.feature && props?.feature?.split?.('_')[0];
                            const variation = props?.variationKey;
                            const [sizeTiles, setSizeTiles] = useState(false);
                            if (optimizely) {
                                /* istanbul ignore next */
                                optimizely
                                    .onReady?.()
                                    .then(() => {
                                        ['v1_web00552_exposed'].includes(variation) && setSizeTiles(true);
                                    })
                                    // eslint-disable-next-line @typescript-eslint/no-empty-function
                                    .catch(() => {});
                            }
                            const equalSize = selectedSku?.size?.centralSizeLabel === selectedSize?.labels[0];
                            const sizeConversionMessage =
                                sizeLabel &&
                                selectedSize &&
                                variation &&
                                !equalSize &&
                                sizeLabel.toLowerCase() !== 'one size' &&
                                !shouldDisableSizeChartAndMessage
                                    ? messages?.sizes?.sizeConversionExperiment?.[experiment]?.({
                                          variation,
                                          sizeLabel: selectedSku?.size?.centralSizeLabel ? selectedSku.size.centralSizeLabel : sizeLabel,
                                          localLanguageType: '',
                                          localSizeValue:
                                              selectedSize.labels.length > 1
                                                  ? `${selectedSize.labels[0]}-${selectedSize.labels[selectedSize.labels.length - 1]}`
                                                  : selectedSize.labels[0],
                                      })
                                    : '';

                            return (
                                <SizeSelect
                                    sizeSelectRef={sizeSelectRef}
                                    className={bem('sizeSelect', [selectedProduct.oneSize && 'isOneSize', applyModelMarginStyle && 'sizeSelectModifier'])}
                                    skus={skus}
                                    value={sizeLabel}
                                    price={price}
                                    messages={messages}
                                    promptError={errors && (!!errors.wishlist || !!errors.addToCart)}
                                    changeSelectedSku={(sizeSku, sizeLabel, delaySizeSelectEvent = false) => {
                                        delaySizeSelectEvent
                                            ? setTimeout(() => {
                                                  safeTrackEvent(trackSizeSelect, sizeSku?.partNumber, sizeLabel, sizeSku?.size.schemas);
                                              }, 1000)
                                            : safeTrackEvent(trackSizeSelect, sizeSku?.partNumber, sizeLabel, sizeSku?.size.schemas);
                                        changeSelectedSku(sizeSku, sizeLabel);
                                        isSizeFitScroll && scrollToSizeFitAccordion();
                                    }}
                                    displayLabel={sizeLabelToggle}
                                    onDropdown={() => {
                                        setActiveEditorialSection(editorialIds.SIZE_AND_FIT);
                                        setToggle(!toggle);
                                    }}
                                    sizeGridFeatureToggle={sizeGridFeatureToggle}
                                    noSizeNotification={noSizeNotification}
                                    variation={variation}
                                    sizeConversionBySku={product.sizeConversionBySku}
                                    sizeConversionMessage={sizeConversionMessage}
                                    disableOneSizeLeft={disableOneSizeLeft}
                                    alwaysGrid={sizeTiles}
                                    sizeTiles={sizeTiles}
                                    hasMeaningfulSize={hasMeaningfulSize}
                                    features={features}
                                />
                            );
                        }}
                    </OptimizelyFeature>
                )}

                {showContactUsCTATop && (
                    <ContactUsCTA
                        className={bem('contactUsCTATop', { addMarginTop: !loyaltyNotification })}
                        productLink={productLink}
                        selectedProduct={selectedProduct}
                        setUser={setUser}
                        isWatchExperts={isWatchExperts}
                        isMRPJewelry={isMRPJewelry}
                        brandId={brandId}
                        imageUrl={imageURL}
                        trackEvent={trackEvent}
                    />
                )}

                {notificationConfig?.enabled && notificationConfig?.locales.includes(locale) && (
                    <Notification type="info" className={bem('pdpNotification')} message={formatNotificationMessage(notificationConfig.message[language])} />
                )}

                {renderModelSize()}

                <CTAButtons
                    setModal={setModal}
                    className={bem('ctaButtons', [modifier, applyNotificationModifier, shouldShowLoyaltyLink && 'hideMargin'])}
                    messages={messages}
                    cart={cartButton}
                    disableAddToBag={!buyable?.canAddToBag || !isProductSizeDisplayable}
                    wishlist={wishlistButton}
                    skuPartNumber={selectedSku && selectedSku.partNumber}
                    selectedProductVariant={product}
                    isProductSizeDisplayable={isProductSizeDisplayable}
                    locale={locale}
                    errors={errors}
                    urlConfig={urlConfig}
                    multipleWishlistsFeatureToggle={!!enableMultipleWishlists}
                    modifier={modifier}
                    setNoSizeNotification={(noSizeNotification) => {
                        if ((scrollToSizeSelectMultipleSizes && sizeSelectRef && skus?.length) || 0 > 1) {
                            scrollToSizeSelect();
                        }
                        setNoSizeNotification(noSizeNotification);
                    }}
                    scrollToSizeSelect={scrollToSizeSelect}
                    preventSkuNotification={preventSkuNotification}
                    errorNotificationTimer={errorNotificationTimer}
                    includeCartWithAddToBag={includeCartWithAddToBag}
                    features={features}
                    selectedProduct={selectedProduct}
                    setUser={setUser}
                    trackEmailUsCTA={() => safeTrackEvent(trackEmailUsCTA, 'help with watch purchase')}
                    trackCallUsCTA={() => safeTrackEvent(trackCallUsCTA)}
                    stickyCTAButton={stickyModifier}
                    showCallUsCTA={showCallUsCTA}
                    fjwThresholdHandler={fjwThresholdHandler}
                    mrpHandler={mrpHandler}
                    napHandler={napHandler}
                    category={tracking?.primaryCategory?.child?.label}
                    isFlexRow
                />

                {shouldShowLoyaltyLink && (
                    <LoyaltyBadgeNotification className={bem('loyaltyMessage')} tier={tier} tracking={props.tracking} isExclusiveBadge={isExclusiveBadge} />
                )}

                {showDeliveryAndReturnsLegal && (
                    <DeliveryAndReturnsLegal
                        className={bem('deliveryAndReturnsLegal')}
                        coreMediaMessage={deliveryReturns}
                        locale={locale}
                        config={config}
                        messages={messages}
                        setModal={setModal}
                    />
                )}

                {!isEip && showFreeShippingThreshold()}
                {fullfilledBy && buyable.canAddToBag ? (
                    <Notification
                        type="info"
                        className={bem('dropshipNotification')}
                        message={
                            <ReactMarkdown
                                // eslint-disable-next-line react/no-children-prop
                                children={messages.dropship.notification({ fullfilledBy })}
                                options={{
                                    overrides: {
                                        a: (props) => (
                                            <a
                                                onClick={() => {
                                                    setActiveEditorialSection(editorialIds.DELIVERY_AND_RETURNS);
                                                    setToggle(!toggle);
                                                }}
                                            >
                                                {props.children}
                                            </a>
                                        ),
                                    },
                                }}
                                unwrapDisallowed
                            />
                        }
                    />
                ) : null}

                <div className={bem('klarnaMessageVariant1', undefined, 'klarnaMessage__container')}>
                    <KlarnaMessage
                        messages={messages}
                        country={country}
                        config={config}
                        showKlarnaMessage={showKlarnaMessage}
                        buyable={buyable}
                        price={price}
                        locale={locale}
                    />
                </div>

                <OptimizelyFeature
                    feature={[
                        'WEB00125_Size_FitProminence_PDP_mobile',
                        'web00124_size_fitprominence_pdp_desktop_exp',
                        'web00091_size_fitprominence_pdp_mrp_desktop',
                        'WEB00093_Size_FitProminence_PDP_MRP_mobile',
                    ]}
                >
                    {(props) => {
                        const { variationKey } = props;
                        return (
                            <EditorialAccordion
                                accordionRef={accordionRef}
                                className={bem('editorialAccordion')}
                                activeSection={activeEditorialSection}
                                toggle={toggle}
                                onOpen={(accordion) => {
                                    safeTrackEvent(trackAccordion, accordion);
                                    if (accordion === 'Size & Fit') {
                                        /* istanbul ignore next */
                                        optimizely?.track?.('interaction_size_fit_accordion');
                                    }
                                }}
                                onLinkClick={(targetURL) => safeTrackEvent(trackAccordionLinkClick, targetURL)}
                                modifier={'pdpAccordion'}
                                setSizeChartModal={setSizeChartModal}
                                features={features}
                                madeIn={madeIn}
                                productNotes={productNotes}
                                sizeConversion={sizeConversion}
                                sizeMeasurement={sizeMeasurement}
                                internal_urls={internal_urls}
                                locale={locale}
                                skus={skus}
                                measurements={measurements}
                                sizeAndFitInformation={sizeAndFitInformation}
                                customerCareUrl={customerCareUrl}
                                isOneSize={isOneSize}
                                selectedPartNumber={product.colourPartNumber}
                                fullfilledBy={fullfilledBy}
                                sizeDetails={messages.sizeAndFit}
                                hasMeaningfulSize={hasMeaningfulSize}
                                considered={tonHandler && consideredBadge}
                                consideredLabels={consideredLabels}
                                sustainabilityDisclaimers={sustainabilityDisclaimers}
                                finalSale={finalSale}
                                variation={variationKey}
                                setIsSizeFitScroll={setIsSizeFitScroll}
                                setDisableOneSizeLeft={setDisableOneSizeLeft}
                                selectedSize={selectedSize}
                                disableOneSizeLeft={disableOneSizeLeft}
                                sizeLabel={sizeLabel}
                                masterCategory={masterCategory}
                                isEip={isEip}
                                enableDeliveryAndReturnCustomMessage={enableDeliveryAndReturnCustomMessage}
                                country={country}
                                colourPartNumber={product.colourPartNumber}
                                messages={messages}
                                optimizely={optimizely}
                                carouselWidth={width}
                                imageViews={imageViews}
                                imageTemplate={imageTemplate}
                                designerIdentifier={product.designerIdentifier}
                                shouldDisableSizeChartAndMessage={shouldDisableSizeChartAndMessage}
                                trackEvent={trackEvent}
                            />
                        );
                    }}
                </OptimizelyFeature>
                <PartNumber className={bem('partNumber')} selectedPartNumber={product.colourPartNumber} messages={messages} />

                {isCompanyInformationLinkEnabled && <CompanyInformation className={bem('companyInformation')} messages={messages} />}

                {!showDeliveryAndReturnsLegal && (
                    <DeliveryAndReturnsSummary
                        className={bem('deliveryAndReturns')}
                        messages={{ deliveryAndReturns: messages.deliveryAndReturns }}
                        onClick={() => setModal('deliveryAndReturns')}
                        config={config}
                        country={country}
                        isEip={isEip}
                    />
                )}

                {showContactUsCTABottom && (
                    <ContactUsCTA
                        className={bem('contactUsCTABottom')}
                        productLink={productLink}
                        selectedProduct={selectedProduct}
                        setUser={setUser}
                        isWatchExperts={isWatchExperts}
                        isMRPJewelry={isMRPJewelry}
                        brandId={brandId}
                        imageUrl={imageURL}
                        trackEvent={trackEvent}
                    />
                )}
            </div>
        </WrapperComp>
    );
}

ProductDetails.propTypes = {
    ogMetaData: shape({ url: string }),
    product: object.isRequired,
    messages: object.isRequired,
    changeSelectedSku: func.isRequired,
    addToCart: func.isRequired,
    addToWishlist: func.isRequired,
    fetchWishlists: func.isRequired,
    wishlists: array,
    locale: string.isRequired,
    actionsLoading: shape({ addToCartLoading: bool, addToWishlistLoading: bool, fetchWishlistsLoading: bool }),
    actionsErrors: object,
    baseUrl: string,
    plpUrlTemplate: string.isRequired,
    generateListingUrl: func.isRequired,
    urlConfig: object,
    internal_urls: array,
    customerCareUrl: string,
    sizeLabelToggle: bool,
    config: shape({ head: shape({ preventSkuNotification: bool, pdp: shape({ enableMultipleWishlists: bool, includeCartWithAddToBag: bool }) }) }),
    features: shape({ hideSizeSelect: bool, accordionTitle: shape({ Component: func }) }),
    sizeAndFitInformation: object,
    measurements: object,
    activeEditorialSection: string,
    toggle: bool,
    setSizeChartModal: func,
    tracking: shape({
        trackedAddToBag: func,
        trackedAddToBagError: func,
        trackedGoToBag: func,
        trackedAddToWishlist: func,
        trackedAddToWishlistError: func,
        safeTrackEvent: func,
        trackedAccordionLinkClick: func,
    }),
    setModal: func,
    onDropDown: func,
    sizeGridFeatureToggle: bool,
    designerLinkOnClick: func,
    language: string,
    redirectToWishlist: func,
    isEnabled: bool,
    pdpUrlTemplate: string,
    setUser: func,
    variationKey: string,
    onModelSizeClick: func,
    promoPrice: object,
};
